import { useState, useRef } from 'react';
import { Share2, Twitter, Facebook, MessageCircle, Instagram, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

// Background images mapped to emotion categories
const MOOD_BACKGROUNDS = {
  'Accepting/Open': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
  'Aliveness/Joy': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&q=80',
  'Angry/Annoyed': 'https://images.unsplash.com/photo-1475070929565-c985b234af0b?w=600&q=80',
  'Connected/Loving': 'https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=600&q=80',
  'Curious': 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80',
  'Despair/Sad': 'https://images.unsplash.com/photo-1499346030926-9a72daac6c63?w=600&q=80',
  'Disconnected/Numb': 'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?w=600&q=80',
  'Embarrassed/Shame': 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80',
  'Fear': 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=600&q=80',
  'Fragile': 'https://images.unsplash.com/photo-1490750967868-88aa4f44baee?w=600&q=80',
  'Grateful': 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=600&q=80',
  'Guilt': 'https://images.unsplash.com/photo-1489549132488-d00b7eee80f1?w=600&q=80',
  'Hopeful': 'https://images.unsplash.com/photo-1495616811223-4d98c6e9c869?w=600&q=80',
  'Powerless': 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=600&q=80',
  'Courageous/Powerful': 'https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=600&q=80',
  'Stressed/Tense': 'https://images.unsplash.com/photo-1477346611705-65d1883cee1e?w=600&q=80',
  'Tender': 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=600&q=80',
  'Unsettled/Doubt': 'https://images.unsplash.com/photo-1446776899648-aa78eefe8ed0?w=600&q=80',
};

const CATEGORY_COLORS = {
  'Accepting/Open': '#3A5A40', 'Aliveness/Joy': '#F59E0B', 'Angry/Annoyed': '#EF4444',
  'Connected/Loving': '#EC4899', 'Curious': '#8B5CF6', 'Despair/Sad': '#6366F1',
  'Disconnected/Numb': '#6B7280', 'Embarrassed/Shame': '#F97316', 'Fear': '#1E293B',
  'Fragile': '#A78BFA', 'Grateful': '#10B981', 'Guilt': '#78716C',
  'Hopeful': '#0EA5E9', 'Powerless': '#64748B', 'Courageous/Powerful': '#DC2626',
  'Stressed/Tense': '#F59E0B', 'Tender': '#F9A8D4', 'Unsettled/Doubt': '#94A3B8',
};

const ShareMoodCard = ({ feeling, category, intensity, quote, userName }) => {
  const [showModal, setShowModal] = useState(false);
  const cardRef = useRef(null);

  const bgImage = MOOD_BACKGROUNDS[category] || MOOD_BACKGROUNDS['Accepting/Open'];
  const accentColor = CATEGORY_COLORS[category] || '#3A5A40';
  const shareUrl = 'https://mindmymojo.com';

  const shareText = `Feeling ${feeling.toLowerCase()} today\n\n"${quote}"\n\nTracking my emotional wellness with Mind My Mojo\n#EmotionalWellness #MindMyMojo`;

  // Share with image — uses native Web Share API on mobile (supports all platforms)
  const shareWithImage = async (platform) => {
    try {
      const blob = await generateShareImage();
      const imageFile = new File([blob], `mindmymojo-${feeling.toLowerCase()}.png`, { type: 'image/png' });
      
      // On mobile: try native share with image file (works for Instagram, WhatsApp, Twitter, etc.)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile && navigator.share && navigator.canShare) {
        const shareData = { files: [imageFile], title: `My Mood: ${feeling}`, text: shareText };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      }
      
      // Desktop fallback: save image + open platform
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mindmymojo-${feeling.toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      await navigator.clipboard.writeText(shareText + '\n\n' + shareUrl).catch(() => {});
      toast.success('Image saved & text copied! Attach the image to your post.');

      setTimeout(() => {
        if (platform === 'twitter') window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
        else if (platform === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank', 'width=600,height=400');
        else if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
        else if (platform === 'instagram') window.location.href = 'instagram://';
      }, 800);
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Failed to share');
    }
  };

  const shareToTwitter = () => shareWithImage('twitter');
  const shareToFacebook = () => shareWithImage('facebook');
  const shareToWhatsApp = () => shareWithImage('whatsapp');
  const shareToInstagram = () => shareWithImage('instagram');

  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleNativeShare = async () => {
    if (!isMobileDevice) {
      // Desktop: always show custom modal with platform buttons
      setShowModal(true);
      return;
    }
    if (navigator.share) {
      try {
        // Try sharing with image first on mobile
        if (navigator.canShare) {
          const imageBlob = await generateShareImage();
          const imageFile = new File([imageBlob], 'my-mood.png', { type: 'image/png' });
          const shareData = {
            title: `My Mood: ${feeling}`,
            text: shareText,
            url: shareUrl,
            files: [imageFile]
          };
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        }
        // Fallback to text-only share
        await navigator.share({ title: `My Mood: ${feeling}`, text: shareText, url: shareUrl });
      } catch (e) {
        if (e.name !== 'AbortError') setShowModal(true);
      }
    } else {
      setShowModal(true);
    }
  };

  // Generate image from canvas for sharing with visuals
  const generateShareImage = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');

      // Draw gradient background (since cross-origin images can't be used in canvas for toBlob)
      const gradient = ctx.createLinearGradient(0, 0, 1080, 1080);
      gradient.addColorStop(0, accentColor + 'FF');
      gradient.addColorStop(0.5, accentColor + 'CC');
      gradient.addColorStop(1, '#1a1a2eFF');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1080, 1080);

      // Add subtle texture dots
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      for (let i = 0; i < 200; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * 1080, Math.random() * 1080, Math.random() * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // "I'M FEELING" label
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = '700 36px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText("I'M FEELING", 540, 340);

      // Feeling name — LARGE and bold
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 90px serif';
      ctx.fillText(feeling, 540, 460);

      // Quote — bigger and more readable
      if (quote) {
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.font = 'italic 34px serif';
        const words = quote.split(' ');
        let line = '"';
        let y = 560;
        for (const word of words) {
          const test = line + word + ' ';
          if (ctx.measureText(test).width > 850) {
            ctx.fillText(line, 540, y);
            line = word + ' ';
            y += 46;
          } else {
            line = test;
          }
        }
        ctx.fillText(line + '"', 540, y);
      }

      // Brand — clearer
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.font = '600 28px sans-serif';
      ctx.fillText('mindmymojo.com', 540, 960);

      // Small dot before brand
      ctx.beginPath();
      ctx.arc(540 - ctx.measureText('mindmymojo.com').width / 2 - 14, 956, 4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fill();

      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shareText + '\n\n' + shareUrl);
    toast.success('Copied to clipboard!');
  };

  const downloadShareImage = async () => {
    try {
      const blob = await generateShareImage();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mindmymojo-${feeling.toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Image saved! Attach it when posting.');
    } catch {
      toast.error('Failed to generate image');
    }
  };

  return (
    <>
      {/* Visual Share Card Preview */}
      <div className="mt-4">
        <div
          ref={cardRef}
          onClick={handleNativeShare}
          className="relative rounded-2xl overflow-hidden cursor-pointer group"
          style={{ aspectRatio: '1/1', maxHeight: '280px' }}
          data-testid="share-mood-card"
        >
          {/* Background Image with blur/haze */}
          <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}CC 0%, ${accentColor}88 50%, rgba(0,0,0,0.6) 100%)` }} />
          
          {/* Content overlay */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
            <p className="text-white/70 text-xs uppercase tracking-widest mb-2">I'm feeling</p>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              {feeling}
            </h3>
            
            {quote && (
              <p className="text-white/90 text-xs md:text-sm italic leading-relaxed max-w-xs mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                "{quote.length > 100 ? quote.substring(0, 100) + '...' : quote}"
              </p>
            )}
            
            <div className="mt-auto flex items-center gap-2 opacity-70">
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
              <span className="text-white text-[10px] font-medium tracking-wider">mindmymojo.com</span>
            </div>
          </div>

          {/* Tap to share - always visible */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md">
            <Share2 className="w-3 h-3 text-warm-charcoal" />
            <span className="text-[10px] font-medium text-warm-charcoal">Tap to share</span>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Share Your Mood</h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded-full hover:bg-stone-100">
                <X className="w-5 h-5 text-warm-charcoal/50" />
              </button>
            </div>

            {/* Mini preview */}
            <div className="relative rounded-xl overflow-hidden mb-4" style={{ height: '120px' }}>
              <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}CC 0%, rgba(0,0,0,0.5) 100%)` }} />
              <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-4">
                <p className="text-white text-lg font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>{feeling}</p>
                <span className="text-white/60 text-[10px] mt-1">mindmymojo.com</span>
              </div>
            </div>

            {/* Platform buttons */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <button onClick={shareToTwitter} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-stone-50 transition-colors" data-testid="share-twitter" style={{ touchAction: 'manipulation' }}>
                <div className="w-10 h-10 rounded-full bg-[#1DA1F2] flex items-center justify-center">
                  <Twitter className="w-5 h-5 text-white" />
                </div>
                <span className="text-[10px] text-warm-charcoal/70">X</span>
              </button>
              <button onClick={shareToFacebook} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-stone-50 transition-colors" data-testid="share-facebook" style={{ touchAction: 'manipulation' }}>
                <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-white" />
                </div>
                <span className="text-[10px] text-warm-charcoal/70">Facebook</span>
              </button>
              <button onClick={shareToInstagram} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-stone-50 transition-colors" data-testid="share-instagram" style={{ touchAction: 'manipulation' }}>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
                <span className="text-[10px] text-warm-charcoal/70">Instagram</span>
              </button>
              <button onClick={shareToWhatsApp} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-stone-50 transition-colors" data-testid="share-whatsapp" style={{ touchAction: 'manipulation' }}>
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-[10px] text-warm-charcoal/70">WhatsApp</span>
              </button>
            </div>

            {/* Save image + Copy buttons */}
            <div className="flex gap-2">
              <Button onClick={downloadShareImage} variant="outline" className="flex-1 text-sm" data-testid="share-save-image">
                Save Image
              </Button>
              <Button onClick={copyToClipboard} variant="outline" className="flex-1 text-sm" data-testid="share-copy">
                Copy Text
              </Button>
            </div>
            <p className="text-[10px] text-warm-charcoal/40 text-center mt-2">
              Save the image, then attach it to your social media post
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareMoodCard;
